import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX } from 'react-icons/fi';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  background: #1e1e1e;
  border: 1px solid #333;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #333;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #fff;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #888;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
  }

  svg {
    font-size: 20px;
  }
`;

const Content = styled.div`
  padding: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #ccc;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4CAF50;
    background: #2e2e2e;
  }

  &::placeholder {
    color: #666;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #333;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 20px;
  background: ${props => props.$primary ? '#4CAF50' : 'transparent'};
  border: 1px solid ${props => props.$primary ? '#4CAF50' : '#555'};
  border-radius: 6px;
  color: ${props => props.$primary ? '#fff' : '#ccc'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$primary ? '#45a049' : 'rgba(255, 255, 255, 0.05)'};
    border-color: ${props => props.$primary ? '#45a049' : '#777'};
    color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default function CreateCategoryModal({ 
  isOpen, 
  onClose, 
  onSave,
  initialValue = '',
  mode = 'create' // 'create' ou 'edit'
}) {
  const [categoryName, setCategoryName] = useState(initialValue);

  useEffect(() => {
    setCategoryName(initialValue);
  }, [initialValue, isOpen]);

  const handleSave = () => {
    const trimmedName = categoryName.trim();
    if (trimmedName) {
      onSave(trimmedName);
      setCategoryName('');
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            {mode === 'create' ? 'Nova Categoria' : 'Renomear Categoria'}
          </Title>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
        </Header>

        <Content>
          <Label htmlFor="category-name">Nome da Categoria</Label>
          <Input
            id="category-name"
            type="text"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Digite o nome da categoria"
            autoFocus
            maxLength={30}
          />
        </Content>

        <Footer>
          <Button onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            $primary 
            onClick={handleSave}
            disabled={!categoryName.trim()}
          >
            {mode === 'create' ? 'Criar' : 'Salvar'}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
}
